import type { AnyAction } from 'redux';
import { DateTime } from '@ksyos/date-time';
import { Alert, Avatar, AvatarButton, Badge, Box, Button, Card, Heading, Text } from '@ksyos/design-system';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { IEmployment } from '@frontend/components/interfaces/employment';
import type { IPractitioner } from '@frontend/components/interfaces/practitioner';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization, PractitionerAccountStatus, ToastType, ValidationErrorCode } from '@common/enums';
import { imageExtensions } from '@common/utils/fileExtensions';
import { getDisplayInitials } from '@common/utils/getDisplayInitials';
import { getDisplayName } from '@common/utils/getDisplayName';
import { isActiveOnDate } from '@common/utils/isActiveOnDate';
import { DangerAlertWhenErrorMessage } from '@frontend/components/elements/common';
import { Modal } from '@frontend/components/elements/modals/Modal';
import { updatePractitionerProfilePicture } from '@frontend/store/authentication/actions';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { hasOneAuthorization, showToast, useAbortableFetch } from '@frontend/utils';

interface IProfileHeaderProps {
    practitionerId: string;
}

export function ProfileHeader(props: IProfileHeaderProps) {
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const fileInput = useRef(null);
    const maxFileSizeInMegaBytes = 10;

    const [practitioner, setPractitioner] = useState<IPractitioner>();
    const [error, setError] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [accountStatus, setAccountStatus] = useState<PractitionerAccountStatus>(PractitionerAccountStatus.activated);
    const [canEdit, setCanEdit] = useState(false);
    const [isSendActivationEmailModalOpen, setIsSendActivationEmailModalOpen] = useState(false);

    const dispatch = useDispatch();

    const loggedInPractitionerId = useSelector((state: IReduxState) => state.authentication.id);

    const abortableFetch = useAbortableFetch();

    useEffect(() => {
        loadPractitioner();
    }, [props.practitionerId]);

    if (!practitioner) {
        return null;
    }

    return (
        <>
            {isManager && !practitioner.isActivated && practitioner.lastLoginOn && (
                <Alert appearance="info">
                    {translateKey('practitioner.practitionerInactiveWarning') + '. '}
                    {hasOneAuthorization([
                        ManagerAuthorization.practitionerAddEdit,
                        ManagerAuthorization.adminsAddEdit,
                    ]) && (
                        <Alert.Link
                            onClick={setPractitionerActive}
                            variant="button"
                        >
                            {translateKey('common.makeActive')}
                        </Alert.Link>
                    )}
                </Alert>
            )}

            {isManager && !practitioner.isActivated && !practitioner.lastLoginOn && (
                <Alert appearance="info">
                    {practitioner.activationEmailSentAt
                        ? translateKey(
                              'profile.lastActivationEmailSentAt',
                              DateTime.from(practitioner.activationEmailSentAt).format('DD-MM-YYYY')
                          )
                        : translateKey('profile.practitionerNotActive')}
                    <br />
                    {hasOneAuthorization([
                        ManagerAuthorization.practitionerAddEdit,
                        ManagerAuthorization.adminsAddEdit,
                    ]) &&
                        !!practitioner.emailAddress && (
                            <Alert.Link
                                onClick={async () => await reloadPractitionerAndShowActivationModal()}
                                variant="button"
                            >
                                {translateKey('profile.resendActivationEmail')}
                            </Alert.Link>
                        )}
                </Alert>
            )}

            <Box sx={{ mt: 'var(--kds-spacing-28)' }}>
                <Card>
                    <Card.Body>
                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="column"
                        >
                            {isUploading && <span>{translateKey('upload.uploading')}...</span>}
                            {!isUploading && canEdit && (
                                <>
                                    <AvatarButton
                                        altText={getDisplayName(practitioner, false, 'nameGivenNameFamily')}
                                        imageUrl={practitioner.profilePictureUrl}
                                        initials={practitioner && getDisplayInitials(practitioner)}
                                        onClick={() => {
                                            openFileSelect();
                                        }}
                                        size="xxlarge"
                                        style={{ marginTop: '-106px', objectFit: 'cover' }}
                                        textOverlay={translateKey('common.edit')}
                                    />
                                    <input
                                        accept={imageExtensions}
                                        className="hidden"
                                        onChange={async (e) => await handleFileSelected(e.target.files)}
                                        ref={fileInput}
                                        type="file"
                                    />
                                </>
                            )}
                            {!isUploading && !canEdit && (
                                <Avatar
                                    altText={getDisplayName(practitioner, false, 'nameGivenNameFamily')}
                                    icon="practitioner-nogender"
                                    imageUrl={practitioner.profilePictureUrl}
                                    size="xxlarge"
                                    style={{ marginTop: '-106px' }}
                                />
                            )}
                            <Heading
                                isSensitiveData={true}
                                mt={4}
                                size="xlarge"
                            >
                                {getDisplayName(practitioner, false, 'nameGivenNameFamily')}
                            </Heading>
                            {isManager && (
                                <Text
                                    mt={4}
                                    size="sm"
                                >
                                    {practitioner.uid}
                                </Text>
                            )}

                            {isManager && accountStatus !== PractitionerAccountStatus.activated && (
                                <Badge
                                    size="medium"
                                    style={{ marginTop: '16px' }}
                                    text={`${translateKey(
                                        accountStatus === PractitionerAccountStatus.terminated
                                            ? 'profile.accountStatusTerminated'
                                            : 'profile.accountStatusNotActivated'
                                    )}`}
                                />
                            )}

                            {error && <DangerAlertWhenErrorMessage errorMessage={error} />}
                        </Box>
                    </Card.Body>
                </Card>
            </Box>

            <Modal
                actionButtons={[
                    <Button
                        onClick={() => setIsSendActivationEmailModalOpen(false)}
                        variant="outline"
                    >
                        {translateKey('common.cancelling')}
                    </Button>,
                    <Button
                        onClick={sendActivationEmail}
                        variant="solid"
                    >
                        {translateKey('common.send')}
                    </Button>,
                ]}
                isOpen={isSendActivationEmailModalOpen}
                onClose={() => setIsSendActivationEmailModalOpen(false)}
                size="small"
                title={'Activetiemail opnieuw verzenden'}
            >
                <Text>
                    {translateKey('profile.activationEmailSentTo')} <b>{practitioner.emailAddress}</b>.{' '}
                    {translateKey('profile.isEmailAddressCorrect')}
                </Text>
            </Modal>
        </>
    );

    function openFileSelect() {
        setError(null);
        (fileInput as any).current.click();
    }

    async function handleFileSelected(files?: FileList | null) {
        if (files && files.length > 0) {
            const file = files[0];

            if (file.size > maxFileSizeInMegaBytes * 1024 * 1024) {
                setError(
                    translateKey(
                        `errorCodes.${ValidationErrorCode.IMAGE_MAX_FILESIZE_EXCEEDED}`,
                        `${maxFileSizeInMegaBytes} MB`
                    )
                );
            } else {
                await updateProfilePicture(file);
            }
        }
    }

    async function updateProfilePicture(file: File) {
        setIsUploading(true);

        try {
            await abortableFetch.uploadFile(`/api/practitioners/${props.practitionerId}/updateProfilePicture`, file);
            const updatedPractitioner = await loadPractitioner();

            if (loggedInPractitionerId === updatedPractitioner.id) {
                dispatch(
                    updatePractitionerProfilePicture(updatedPractitioner.profilePictureUrl) as unknown as AnyAction
                );
            }
        } catch (err) {
            setError(translateKey('validation.general'));
        }

        setIsUploading(false);
    }

    async function loadPractitioner() {
        const result = await abortableFetch.fetchJson(
            `/api/practitioners/${props.practitionerId}/?includeItemAuthorizations=canUpdateProfilePicture&includeProfileThumbnail=true`,
            'GET'
        );
        const employments = await abortableFetch.fetchJson(
            `/api/user/employments/?practitionerId=${props.practitionerId}&onlyActives=false`,
            'GET'
        );
        setPractitioner(result.json);
        setCanEdit(result.json.authorization.canUpdateProfilePicture);

        await updateAccountStatus(result.json, employments.json?.items);

        return result.json;
    }

    async function reloadPractitionerAndShowActivationModal() {
        // load again the practitioner as the email may have changed
        await loadPractitioner();
        setIsSendActivationEmailModalOpen(true);
    }

    async function setPractitionerActive() {
        await abortableFetch.fetchJson(`/api/practitioners/${props.practitionerId}/active`, 'POST', {
            isActivated: true,
        });
        await loadPractitioner();
    }

    async function sendActivationEmail() {
        setIsSendActivationEmailModalOpen(false);

        const result = await fetchJson(`/api/practitioners/${props.practitionerId}/activationEmail`, 'POST');
        if (result.status === 200) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
            showToast(ToastType.success, translateKey('profile.activationEmailResentTo', practitioner?.emailAddress!));
        }

        await loadPractitioner();
    }

    async function updateAccountStatus(practitionerInfo: IPractitioner, employments: IEmployment[]) {
        let status = PractitionerAccountStatus.activated;
        const isTerminated = employments.every(
            (employment) => employment.endDate && DateTime.from(employment.endDate).isBefore(DateTime.now())
        );
        const hasActiveEmployment = employments.some((employment) => isActiveOnDate(employment, new Date()));

        if (isTerminated) {
            status = PractitionerAccountStatus.terminated;
        } else if (!practitionerInfo.isActivated && hasActiveEmployment) {
            status = PractitionerAccountStatus.notActivated;
        }
        setAccountStatus(status);
    }
}
